import {AsyncInlineError, inlineError} from '@s-ui/decorators'

import {FuelTypeListValueObject} from '../ValueObjects/FuelTypeListValueObject.js'
import {GetMakeModelSemanticsValueObject} from '../ValueObjects/GetMakeModelSemanticsValueObject.js'
import {ImageValueObject} from '../ValueObjects/ImageValueObject.js'
import {ListBodyTypesOptionsValueObject} from '../ValueObjects/ListBodyTypesOptionsValueObject.js'
import {ListFuelTypesOptionsValueObject} from '../ValueObjects/ListFuelTypesOptionsValueObject.js'
import {ListImagesValueObject} from '../ValueObjects/ListImagesValueObject.js'
import {ListMakesOptionsValueObject} from '../ValueObjects/ListMakesOptionsValueObject.js'
import {ListModelVehiclesValueObject} from '../ValueObjects/ListModelVehiclesValueObject.js'
import {ListPriceRangeOptionsValueObject} from '../ValueObjects/ListPriceRangeOptionsValueObject.js'
import {ListSeatsOptionsValueObject} from '../ValueObjects/ListSeatsOptionsValueObject.js'
import {MakesWithModelsListVehiclesValueObject} from '../ValueObjects/MakesWithModelsListVehiclesValueObject.js'
import {ModelDetailContentV2ValueObject} from '../ValueObjects/ModelDetailContentV2ValueObject.js'
import {SuggestedVehiclesValueObject} from '../ValueObjects/SuggestedVehiclesValueObject.js'
import {VehicleAiSeoContentValueObject} from '../ValueObjects/VehicleAiSeoContentValueObject.js'
import {VehicleConfigurationValueObject} from '../ValueObjects/VehicleConfigurationValueObject.js'
import {VehicleContentValueObject} from '../ValueObjects/VehicleContentValueObject.js'
import {VehiclesRepository} from './VehiclesRepository.js'

/**
 * @class
 * @implements VehicleRepository
 */
export class HTTPVehiclesRepository extends VehiclesRepository {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Config} deps.config
   * @param {Fetcher} deps.fetcher
   * @param {Fetcher} deps.fakeFetcher
   * @param {Fetcher} deps.fetchOrFail
   * @param {Function} deps.brandModelInfoValueObjectFactory
   * @param {Function} deps.fromCompetitorsRawDataToRelatedVehiclesListValueObjectMapper
   * @param {Function} deps.fromInfoVehicleRawDataToInfoVehicleValueObjectMapper
   * @param {Function} deps.fromMakesRawDataToMakesValueObjectMapper
   * @param {Function} deps.fromMarketedMakesRawDataToMarketedMakesValueObjectMapper
   * @param {Function} deps.fromModelInfoRawDataToModelInfoValueObjectMapper
   * @param {Function} deps.fromModelsHttpRepositoryDataToModelsValueObjectMapper
   * @param {Function} deps.fromModelsRawDataToModelsValueObjectMapper
   * @param {Function} deps.fromSearchUrlParamToSearchCodeValueObjectMapper
   * @param {Function} deps.fromSpecsListRawDataToSpecsListValueObjectMapper
   * @param {Function} deps.fromSubscriptionModelsRawDataToSubscriptionModelsListValueObjectMapper
   * @param {Function} deps.fromVehicleInfoRawDataToVehicleInfoValueObjectMapper
   * @param {Function} deps.fromVehicleVersionDetailRawDataToVehicleInfoValueObjectMapper
   * @param {Function} deps.fromVehicleListRawDataToVehicleListVOMapper
   * @param {Function} deps.fromVersionInfoRawDataToVersionInfoValueObjectMapper
   * @param {Function} deps.genericVehiclesErrorFactory
   * @param {Function} deps.getBodyTypeInfoVehiclesError
   * @param {Function} deps.getBrandModelInfoVehiclesError
   * @param {Function} deps.getMarketedMakesVehiclesError
   * @param {Function} deps.getModelInfoVehiclesError
   * @param {Function} deps.getModelsGroupedByBodyTypeDoorsVehiclesError
   * @param {Function} deps.getVersionInfoVehiclesError
   * @param {Function} deps.notFoundCompetitorsVehiclesErrorFactory
   * @param {Function} deps.notFoundSpecsVehiclesErrorFactory
   * @param {Function} deps.notFoundVehiclesErrorFactory
   * @param {Function} deps.searchVehiclesResultsValueObjectFactory
   * @param {Function} deps.log
   * @param {Function} deps.toQueryString
   */
  constructor({
    config,
    fetcher,
    fakeFetcher,
    fetchOrFail,
    log,
    toQueryString,
    brandModelInfoValueObjectFactory,
    fromCompetitorsRawDataToRelatedVehiclesListValueObjectMapper,
    fromInfoVehicleRawDataToInfoVehicleValueObjectMapper,
    fromMakesRawDataToMakesListValueObjectMapper,
    fromMarketedMakesRawDataToMarketedMakesValueObjectMapper,
    fromRawsAIFaqsToFaqsListVOMapper,
    fromModelInfoRawDataToModelInfoValueObjectMapper,
    fromModelsHttpRepositoryDataToModelsValueObjectMapper,
    fromModelsRawDataToModelsListValueObjectMapper,
    fromModelVersionsRawDataToModelVersionsVOMapper,
    fromRawDistributorCarouselsToCarouselsInfoVOMapper,
    fromSearchToSearchVehiclesRequestValueObjectMapper,
    fromSearchUrlParamToSearchCodeValueObjectMapper,
    fromSpecsListRawDataToSpecsListValueObjectMapper,
    fromSubscriptionMakesRawDataToMakesListValueObjectMapper,
    fromSubscriptionModelsRawDataToSubscriptionModelsListValueObjectMapper,
    fromVehicleInfoRawDataToVehicleInfoValueObjectMapper,
    fromVehicleVersionDetailRawDataToVehicleInfoValueObjectMapper,
    fromVehicleListRawDataToVehicleListVOMapper,
    fromVersionInfoRawDataToVersionInfoValueObjectMapper,
    genericVehiclesErrorFactory,
    getBodyTypeInfoVehiclesError,
    getBrandModelInfoVehiclesError,
    getMarketedMakesVehiclesError,
    getModelDetailContentError,
    getModelDetailContentNotFoundError,
    getModelInfoVehiclesError,
    getModelsGroupedByBodyTypeDoorsVehiclesError,
    getSubscriptionMakesNotFoundVehiclesError,
    getSubscriptionMakesVehiclesError,
    getSubscriptionModelsNotFoundVehiclesError,
    getSubscriptionModelsVehiclesError,
    getVersionInfoVehiclesError,
    notFoundCompetitorsVehiclesErrorFactory,
    notFoundVehiclesErrorFactory,
    searchVehiclesResultsValueObjectFactory,
    searchRequestJSONToSearchParamsRequestJSONMapper,
    specsListValueObjectFactory
  }) {
    super({
      config
    })

    this._config = config
    this._fetcher = fetcher
    this._fakeFetcher = fakeFetcher
    this._fetchOrFail = fetchOrFail
    this._apiUrl = config.get('API_URL')
    this._log = log
    this._toQueryString = toQueryString
    this._fromRawsAIFaqsToFaqsListVOMapper = fromRawsAIFaqsToFaqsListVOMapper
    this._fromCompetitorsRawDataToRelatedVehiclesListValueObjectMapper =
      fromCompetitorsRawDataToRelatedVehiclesListValueObjectMapper
    this._fromInfoVehicleRawDataToInfoVehicleValueObjectMapper = fromInfoVehicleRawDataToInfoVehicleValueObjectMapper
    this._fromMarketedMakesRawDataToMarketedMakesValueObjectMapper =
      fromMarketedMakesRawDataToMarketedMakesValueObjectMapper
    this._fromMakesRawDataToMakesListValueObjectMapper = fromMakesRawDataToMakesListValueObjectMapper
    this._fromModelsRawDataToModelsListValueObjectMapper = fromModelsRawDataToModelsListValueObjectMapper
    this._fromModelsHttpRepositoryDataToModelsValueObjectMapper = fromModelsHttpRepositoryDataToModelsValueObjectMapper
    this._fromSearchUrlParamToSearchCodeValueObjectMapper = fromSearchUrlParamToSearchCodeValueObjectMapper
    this._fromModelInfoRawDataToModelInfoValueObjectMapper = fromModelInfoRawDataToModelInfoValueObjectMapper
    this._fromVersionInfoRawDataToVersionInfoValueObjectMapper = fromVersionInfoRawDataToVersionInfoValueObjectMapper
    this._fromVehicleInfoRawDataToVehicleInfoValueObjectMapper = fromVehicleInfoRawDataToVehicleInfoValueObjectMapper
    this._fromVehicleVersionDetailRawDataToVehicleInfoValueObjectMapper =
      fromVehicleVersionDetailRawDataToVehicleInfoValueObjectMapper
    this._fromSpecsListRawDataToSpecsListValueObjectMapper = fromSpecsListRawDataToSpecsListValueObjectMapper
    this._fromSearchToSearchVehiclesRequestValueObjectMapper = fromSearchToSearchVehiclesRequestValueObjectMapper
    this._fromSubscriptionMakesRawDataToMakesListValueObjectMapper =
      fromSubscriptionMakesRawDataToMakesListValueObjectMapper
    this._fromSubscriptionModelsRawDataToSubscriptionModelsListValueObjectMapper =
      fromSubscriptionModelsRawDataToSubscriptionModelsListValueObjectMapper
    this._fromVehicleListRawDataToVehicleListVOMapper = fromVehicleListRawDataToVehicleListVOMapper
    this._fromModelVersionsRawDataToModelVersionsVOMapper = fromModelVersionsRawDataToModelVersionsVOMapper
    this._fromRawDistributorCarouselsToCarouselsInfoVOMapper = fromRawDistributorCarouselsToCarouselsInfoVOMapper
    this._brandModelInfoValueObjectFactory = brandModelInfoValueObjectFactory
    this._searchVehiclesResultsValueObjectFactory = searchVehiclesResultsValueObjectFactory
    this._searchRequestJSONToSearchParamsRequestJSONMapper = searchRequestJSONToSearchParamsRequestJSONMapper
    this.__specsListValueObjectFactory = specsListValueObjectFactory
    this._getBrandModelInfoVehiclesError = getBrandModelInfoVehiclesError
    this._getModelsGroupedByBodyTypeDoorsVehiclesError = getModelsGroupedByBodyTypeDoorsVehiclesError
    this._getMarketedMakesVehiclesError = getMarketedMakesVehiclesError
    this._getModelDetailContentError = getModelDetailContentError
    this._getModelDetailContentNotFoundError = getModelDetailContentNotFoundError
    this._getModelInfoVehiclesError = getModelInfoVehiclesError
    this._getBodyTypeInfoVehiclesError = getBodyTypeInfoVehiclesError
    this._getSubscriptionMakesNotFoundVehiclesError = getSubscriptionMakesNotFoundVehiclesError
    this._getSubscriptionModelsNotFoundVehiclesError = getSubscriptionModelsNotFoundVehiclesError
    this._getSubscriptionModelsVehiclesError = getSubscriptionModelsVehiclesError
    this._getSubscriptionMakesVehiclesError = getSubscriptionMakesVehiclesError
    this._getVersionInfoVehiclesError = getVersionInfoVehiclesError
    this._genericVehiclesErrorFactory = genericVehiclesErrorFactory
    this._notFoundCompetitorsVehiclesErrorFactory = notFoundCompetitorsVehiclesErrorFactory
    this._notFoundVehiclesErrorFactory = notFoundVehiclesErrorFactory
    this._FILTER_NAMES = config.get('search').FILTER_NAMES
    this._HTTP_STATUS_CODES = this._config.get('COMMON').HTTP_STATUS_CODES
    this._ERRORS = this._config.get('COMMON').ERRORS
  }

  getBrandModelInfo = async ({makeId, modelId}) => {
    if (!makeId || !modelId) return

    try {
      const {
        data: {title: name, text: description, imageUrl}
      } = await this._fetcher.get(`${this._config.get('API_URL')}/wdyl-header?makeId=${makeId}&modelId=${modelId}`)
      return this._brandModelInfoValueObjectFactory({
        name,
        description,
        imageUrl
      })
    } catch (error) {
      throw this._getBrandModelInfoVehiclesError(
        '[HTTPVehiclesRepository#getBrandModelInfo] Get BrandModelInfoVehicles error'
      )
    }
  }

  @inlineError
  async getCarouselsInfo() {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(
            `[HTTPVehiclesRepository#getCarouselsInfo] GetCarouselsInfo not found:  ${message}`
          ),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(
            `[HTTPVehiclesRepository#getCarouselsInfo] GetCarouselsInfo error:  ${message}`
          )
      })
      .get(`${this._config.get('API_URL')}/vehicle/distributor/carousels`)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._fromRawDistributorCarouselsToCarouselsInfoVOMapper.map({
      data
    })
  }

  @inlineError
  async getSizesCarouselsInfo() {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(
            `[HTTPVehiclesRepository#getSizesCarouselsInfo] GetSizesCarouselsInfo not found:  ${message}`
          ),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(
            `[HTTPVehiclesRepository#getSizesCarouselsInfo] GetSizesCarouselsInfo error:  ${message}`
          )
      })
      .get(`${this._config.get('API_URL')}/vehicle/distributor/dimensions/carousels`)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._fromRawDistributorCarouselsToCarouselsInfoVOMapper.map({
      data
    })
  }

  @inlineError
  async getVehicleContentFromAi(getVehicleContentRequest) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const {makeId, modelId} = getVehicleContentRequest.value

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleContentFromAi] not found:  ${message}`),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleContentFromAi] error:  ${message}`)
      })
      .get(`${this._config.get('API_URL')}/ai/model-detail/make/${makeId}/model/${modelId}`)

    if (error) return Promise.reject(error)

    const {data} = response

    return VehicleContentValueObject.createFromPrimitive({...data})
  }

  @AsyncInlineError()
  async getVehicleAiSeoContent(getVehicleAiSeoContentRequest) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const {makeId, modelId} = getVehicleAiSeoContentRequest.value

    const url = modelId
      ? `${this._config.get('API_URL')}/ai/seo-content/make-detail/make/${makeId}/model/${modelId}`
      : `${this._config.get('API_URL')}/ai/seo-content/make-detail/make/${makeId}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleAiSeoContent] not found: ${message}`),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleAiSeoContent] error: ${message}`)
      })
      .get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    return VehicleAiSeoContentValueObject.createFromApi(data)
  }

  @AsyncInlineError()
  async getVehicleConfiguration(getVehicleConfigurationRequest) {
    const {NOT_FOUND, NO_CONTENT, BAD_REQUEST} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const {vehicleId, zipcode, legalConditionsAccepted} = getVehicleConfigurationRequest.toJSON()

    const queryParams = this._toQueryString({
      legalConditionsAccepted
    })

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleConfiguration] not found:  ${message}`),
        [BAD_REQUEST]: ({message}) =>
          this._notFoundVehiclesErrorFactory(
            `[HTTPVehiclesRepository#getVehicleConfiguration] bad request:  ${message}`
          ),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleConfiguration] error:  ${message}`)
      })
      .get(
        `${this._config.get(
          'API_URL'
        )}/configuration/resolver/vehicle/${vehicleId}/postalcode/${zipcode}?${queryParams}`
      )

    if (error) return Promise.reject(error)

    if (response.status === NO_CONTENT) {
      return Promise.reject(
        this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleConfiguration] no content`)
      )
    }

    const {data} = response

    return VehicleConfigurationValueObject.createFromApi({config: this._config, data})
  }

  @inlineError
  async getFuelTypes(getFuelTypesRequest) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const DEFAULT_CATEGORY1_ID = this._config.get('search').DEFAULT_CATEGORY1_ID

    const {makeId, modelId} = getFuelTypesRequest.toJSON()

    const queryParams = this._toQueryString({
      makeId,
      modelId,
      sectionId: DEFAULT_CATEGORY1_ID,
      onlyInCommercialization: true
    })

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getFuelTypes] not found:  ${message}`),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(`[HTTPVehiclesRepository#getFuelTypes] error:  ${message}`)
      })
      .get(`${this._config.get('API_URL')}/vehicle-specs/v1/fuel-types?${queryParams}`)

    if (error) return Promise.reject(error)

    const {data} = response

    return FuelTypeListValueObject.createFromPrimitives({value: data})
  }

  _getVehicleInfo = ({endpoint, params}) => {
    if (!endpoint || !params) return

    return this._fakeFetcher.get(endpoint, {params})
  }

  _getDepsFromField = field => {
    switch (field) {
      case this._FILTER_NAMES.MAKE:
        return {
          endpoint: `${this._config.get('API_URL')}/makes`,
          rawDataMapper: this._fromMakesRawDataToMakesListValueObjectMapper
        }
      case this._FILTER_NAMES.MODEL:
        return {
          endpoint: `${this._config.get('API_URL')}/models`,
          rawDataMapper: this._fromModelsRawDataToModelsListValueObjectMapper
        }
      default:
        return {
          endpoint: `${this._config.get('API_URL')}/vehicleInfo/${field}/`,
          rawDataMapper: this._fromInfoVehicleRawDataToInfoVehicleValueObjectMapper
        }
    }
  }

  getInfoVehicle = async ({field, params}) => {
    const {endpoint, rawDataMapper} = this._getDepsFromField(field)
    const {
      data: {items}
    } = await this._getVehicleInfo({endpoint, params})
    return rawDataMapper.map({
      items
    })
  }

  @inlineError
  async getVehiclesInfoByPlate({getVehiclesInfoByPlateRequest}) {
    const {plate: carPlate} = getVehiclesInfoByPlateRequest.toJSON()
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const url = `${this._config.get('API_URL')}/vehicle/${carPlate}/car-specs?ordered=true`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._notFoundVehiclesErrorFactory(message),
        [GENERIC]: ({message}) => this._genericVehiclesErrorFactory(message)
      })
      .get(url)

    if (error) return Promise.reject(error)

    return this._fromVehicleListRawDataToVehicleListVOMapper.map({
      carPlate,
      ...response.data
    })
  }

  @inlineError
  async getModelDetailContent(getVehicleContentRequest) {
    const {NOT_FOUND, NO_CONTENT} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const queryParams = getVehicleContentRequest.toQueryString()
    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._getModelDetailContentNotFoundError(
            `[HTTPVehiclesRepository#getModelDetailContent] GetModelDetailContent not found:  ${message}`
          ),
        [NO_CONTENT]: ({message}) => {
          return this._getModelDetailContentNotFoundError(
            `[HTTPVehiclesRepository#getModelDetailContent] GetModelDetailContent not found:  ${message}`
          )
        },
        [GENERIC]: ({message}) =>
          this._getModelDetailContentError(
            `[HTTPVehiclesRepository#getModelDetailContent] GetModelDetailContent error:  ${message}`
          )
      })
      .get(`${this._config.get('CONTENTFUL_API_URL')}/model-detail?${queryParams}`)
    if (error) {
      return Promise.reject(error)
    }

    const {data} = response

    return ModelDetailContentV2ValueObject.createFromContentfulData({config: this._config, contentfulData: data})
  }

  @inlineError
  async getMakeModelFaqs(getMakeModelFaqsRequest) {
    const {context, makeId, modelId, make, model, path} = getMakeModelFaqsRequest.toJSON()

    const {NOT_FOUND, NO_CONTENT} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const queryParams = this._toQueryString({
      context,
      makeId,
      modelId,
      ...(make && {make}),
      ...(model && {model})
    })
    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._getModelDetailContentNotFoundError(
            `[HTTPVehiclesRepository#getMakeModelFaqs] GetModelDetailContent not found:  ${message}`
          ),
        [NO_CONTENT]: ({message}) => {
          return this._getModelDetailContentNotFoundError(
            `[HTTPVehiclesRepository#getMakeModelFaqs] GetModelDetailContent not found:  ${message}`
          )
        },
        [GENERIC]: ({message}) =>
          this._getModelDetailContentError(
            `[HTTPVehiclesRepository#getMakeModelFaqs] GetModelDetailContent error:  ${message}`
          )
      })
      .get(`${this._config.get('API_URL')}/faqs?${queryParams}`)
    if (error) {
      return Promise.reject(error)
    }

    const {data} = response

    return this._fromRawsAIFaqsToFaqsListVOMapper.map({...data, path})
  }

  @inlineError
  async getMakeModelSemantics(getMakeModelSemanticsRequest) {
    const {make, model} = getMakeModelSemanticsRequest.value

    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const url = `${this._config.get('API_URL')}/vehicle/modeldetail/semantic/${make}/${model}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._notFoundVehiclesErrorFactory(message),
        [GENERIC]: ({message}) => this._genericVehiclesErrorFactory(message)
      })
      .get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    return GetMakeModelSemanticsValueObject.create(data)
  }

  getMarketedMakes = async ({urlPrefix}) => {
    try {
      const {
        data: {relations: makesList}
      } = await this._fetcher.get(`${this._config.get('API_URL')}/relation/allCurrentMakes`)
      const makesListVO = this._fromMarketedMakesRawDataToMarketedMakesValueObjectMapper.setParams({urlPrefix}).map({
        makesList
      })
      return makesListVO
    } catch (error) {
      throw this._getMarketedMakesVehiclesError(
        '[HTTPVehiclesRepository#getMarketedMakes] Get MarketedMakesVehicles error'
      )
    }
  }

  getModelsGroupedByBodyTypeDoors = async ({makeUrlParam}) => {
    const {searchCode: makeCode} = this._fromSearchUrlParamToSearchCodeValueObjectMapper
      .map({searchUrlParam: makeUrlParam})
      .toJSON()

    const {DEFAULT_CATEGORY1_ID} = this._config.get('search')

    const queryParams = this._toQueryString({
      makeCode,
      category1Id: DEFAULT_CATEGORY1_ID
    })

    try {
      const {
        data: {datasheet: modelsList}
      } = await this._fetcher.get(`${this._config.get('API_URL')}/datasheet/?${queryParams}`)

      const modelsListVO = this._fromModelsHttpRepositoryDataToModelsValueObjectMapper.map({
        modelsList,
        makeUrlParam
      })

      return modelsListVO
    } catch (error) {
      throw this._getModelsGroupedByBodyTypeDoorsVehiclesError(
        '[HTTPVehiclesRepository#getModelsGroupedByBodyTypeDoors] Get ModelsGroupedByBodyTypeDoorsVehicles error'
      )
    }
  }

  getModelInfo = async ({makeCode, modelCode, size}) => {
    const queryParams = this._toQueryString({
      makeCode,
      modelCode,
      size
    })
    try {
      const {data: modelInfo} = await this._fetcher.get(
        `${this._config.get('API_URL')}/datasheet/make-models/?${queryParams}`
      )

      return this._fromModelInfoRawDataToModelInfoValueObjectMapper.map({
        modelInfo,
        makeCode,
        modelCode
      })
    } catch (error) {
      throw this._getModelInfoVehiclesError('[HTTPVehiclesRepository#getModelInfo] Get ModelInfoVehicles error')
    }
  }

  getBodyTypeInfo = async ({makeCode, modelCode, bodyTypeCode, doorsCode, size}) => {
    const queryParams = this._toQueryString({
      makeCode,
      modelCode,
      bodyTypeCode,
      doors: doorsCode,
      size
    })
    try {
      const {data: modelInfo} = await this._fetcher.get(
        `${this._config.get('API_URL')}/datasheet/make-model-bodytypes/?${queryParams}`
      )
      return this._fromModelInfoRawDataToModelInfoValueObjectMapper.map({
        modelInfo,
        makeCode,
        modelCode,
        bodyTypeCode,
        doorsCode
      })
    } catch (error) {
      throw this._getBodyTypeInfoVehiclesError(
        '[HTTPVehiclesRepository#getBodyTypeInfo] Get BodyTypeInfoVehicles error'
      )
    }
  }

  @inlineError
  async getCompetitors({vehicleEntity}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const vehicleId = vehicleEntity.getId()

    const url = `${this._config.get('API_URL')}/competitors?uniqueVehicleId=${vehicleId}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._notFoundVehiclesErrorFactory(message),
        [GENERIC]: ({message}) => this._genericVehiclesErrorFactory(message)
      })
      .get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._fromCompetitorsRawDataToRelatedVehiclesListValueObjectMapper.map(data)
  }

  @inlineError
  async getSubscriptionMakes() {
    const URL_PROTOCOL = this._config.get('URL_PROTOCOL')
    const API_GW_SUBDOMAIN_CNET_CONTENT = this._config.get('API_GW_SUBDOMAIN_CNET_CONTENT')
    const API_GW_DOMAIN = this._config.get('API_GW_DOMAIN')

    const subscriptionApiUrl = `${URL_PROTOCOL}${API_GW_SUBDOMAIN_CNET_CONTENT}${API_GW_DOMAIN}`

    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._getSubscriptionMakesNotFoundVehiclesError(message),
        [GENERIC]: ({message}) => this._getSubscriptionMakesVehiclesError(message)
      })
      .get(`${subscriptionApiUrl}/makes`)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._fromSubscriptionMakesRawDataToMakesListValueObjectMapper.map(data)
  }

  @inlineError
  async getSubscriptionModels() {
    const URL_PROTOCOL = this._config.get('URL_PROTOCOL')
    const API_GW_SUBDOMAIN_CNET_CONTENT = this._config.get('API_GW_SUBDOMAIN_CNET_CONTENT')
    const API_GW_DOMAIN = this._config.get('API_GW_DOMAIN')

    const subscriptionApiUrl = `${URL_PROTOCOL}${API_GW_SUBDOMAIN_CNET_CONTENT}${API_GW_DOMAIN}`

    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._getSubscriptionModelsNotFoundVehiclesError(message),
        [GENERIC]: ({message}) => this._getSubscriptionModelsVehiclesError(message)
      })
      .get(`${subscriptionApiUrl}/models`)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._fromSubscriptionModelsRawDataToSubscriptionModelsListValueObjectMapper.map(data)
  }

  getVersionInfo = async ({makeCode, modelCode, bodyTypeCode, doorsCode, hpCode, versionId, path, year}) => {
    try {
      let url = `${this._config.get(
        'API_URL'
      )}/datasheet/model-versions/?makeCode=${makeCode}&modelCode=${modelCode}&bodyTypeCode=${bodyTypeCode}&doors=${doorsCode}&hp=${hpCode}&versionId=${versionId}`

      if (year) {
        url = `${url}&year=${year}`
      }

      const {data: versionInfo} = await this._fetcher.get(url)

      return this._fromVersionInfoRawDataToVersionInfoValueObjectMapper.map({
        versionInfo,
        makeCode,
        modelCode,
        bodyTypeCode,
        path
      })
    } catch (error) {
      throw this._getVersionInfoVehiclesError('[HTTPVehiclesRepository#getVersionInfo] Get VersionInfoVehicles error')
    }
  }

  @inlineError
  async getVehicleInfo({grouped, make, model, vehicleId}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const APP_NAME = this._config.get('APP_NAME')
    const APP_NAME_COCHES = this._config.get('APP_NAME_COCHES')

    // v2 endpoint will return grouped specifications
    const url = grouped
      ? `${this._config.get('API_URL')}/v2/datasheet/unique-model/${vehicleId}`
      : `${this._config.get('API_URL')}/datasheet/unique-model/${vehicleId}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._notFoundVehiclesErrorFactory(message),
        [GENERIC]: ({message}) => this._genericVehiclesErrorFactory(message)
      })
      .get(url)

    if (error) return Promise.reject(error)

    const {data} = response
    let vehicleSpecsV2Data = {}
    if (APP_NAME === APP_NAME_COCHES) {
      const vehicleSpecsURL = `${this._config.get('API_URL')}/vehicle-specs/v2/vehicles/${vehicleId}`
      /**
       * Once we start receiving the FuelTypeId from backend, we can make this call
       * conditional depending on the vehicle fuelType (Hybrid, PHEV or Electric).
       * Also, when backend migrates all info to this endpoint, we can get rid of the
       * two endpoints above and only keep this one.
       */
      const [getVehicleSpecsV2Error, getVehicleSpecsV2Response] = await this._fetchOrFail
        .withErrorsFactories({
          [NOT_FOUND]: ({message}) => this._notFoundVehiclesErrorFactory(message),
          [GENERIC]: ({message}) => this._genericVehiclesErrorFactory(message)
        })
        .get(vehicleSpecsURL)

      if (getVehicleSpecsV2Error) return Promise.reject(getVehicleSpecsV2Error)

      vehicleSpecsV2Data = getVehicleSpecsV2Response.data
    }

    return this._fromVehicleInfoRawDataToVehicleInfoValueObjectMapper.setParams({grouped}).map({
      vehicleInfo: data,
      make,
      model,
      ...(vehicleSpecsV2Data.electricFeatures && {
        electricFeatures: vehicleSpecsV2Data.electricFeatures
      })
    })
  }

  @inlineError
  async getVehicleInfoV2({grouped, vehicleId}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    /**
     * New endpoint which gets all technical information (including electrical features)
     * We can now avoid having to make 2 different calls and aggregate the responses.
     */
    const vehicleURL = `${this._config.get('API_URL')}/vehicle/versiondetail/${vehicleId}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._notFoundVehiclesErrorFactory(message),
        [GENERIC]: ({message}) => this._genericVehiclesErrorFactory(message)
      })
      .get(vehicleURL)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._fromVehicleVersionDetailRawDataToVehicleInfoValueObjectMapper.setParams({grouped}).map(data)
  }

  @inlineError
  async getModelVersions({getModelVersionsRequestValueObject}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const {competitorsCount, makeId, modelId, pageName, onlyCurrentVersions, includeInsights} =
      getModelVersionsRequestValueObject.toJSON()

    const queryParams = this._toQueryString({
      makeId,
      modelId,
      onlyCurrentVersions
    })

    const url = `${this._config.get('API_URL')}/vehicle/modeldetail/versions-competitors-angled-image?${queryParams}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._notFoundVehiclesErrorFactory(message),
        [GENERIC]: ({message}) => this._genericVehiclesErrorFactory(message)
      })
      .get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._fromModelVersionsRawDataToModelVersionsVOMapper
      .setParams({pageName, competitorsCount, includeInsights})
      .map(data)
  }

  @inlineError
  async getModelImageByMakeModel({getModelImageRequestValueObject}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const {makeId, modelId, angle} = getModelImageRequestValueObject.toJSON()

    const queryParams = this._toQueryString({
      makeId,
      modelId,
      angle
    })

    const url = `${this._config.get('API_URL')}/api/technical-data/models-imagesByMakeModelAngle?${queryParams}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._notFoundVehiclesErrorFactory(message),
        [GENERIC]: ({message}) => this._genericVehiclesErrorFactory(message)
      })
      .get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    return ImageValueObject.createFromApi(data)
  }

  @inlineError
  async getSpecs({nextField, url}) {
    const {NOT_FOUND, NO_CONTENT} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._notFoundVehiclesErrorFactory(message),
        [GENERIC]: ({message}) => this._genericVehiclesErrorFactory(message)
      })
      .get(url)

    if (error) return Promise.reject(error)

    if (response.status === NO_CONTENT) {
      return Promise.reject(
        this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleConfiguration] no content`)
      )
    }

    const {
      data: {items, versions}
    } = response

    return this._fromSpecsListRawDataToSpecsListValueObjectMapper.setParams({nextField}).map({
      items: items || versions
    })
  }

  @inlineError
  async getVehicleExtras(getVehicleExtrasRequest) {
    const {NOT_FOUND, NO_CONTENT} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const {vehicleId, field} = getVehicleExtrasRequest.toJSON()

    const url = `${this._config.get('API_URL')}/vehicle-specs/v1/vehicle/${vehicleId}/${field}/configurator`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._notFoundVehiclesErrorFactory(message),
        [GENERIC]: ({message}) => this._genericVehiclesErrorFactory(message)
      })
      .get(url)

    if (error) return Promise.reject(error)

    if (response.status === NO_CONTENT) {
      return Promise.reject(this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleExtras] no content`))
    }

    const {
      data: {items}
    } = response

    return this._fromSpecsListRawDataToSpecsListValueObjectMapper.setParams({nextField: field}).map({
      items
    })
  }

  @inlineError
  async getSearchResults({search}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const url = `${this._config.get('API_URL')}/datasheet/versions/search`

    const body = this._fromSearchToSearchVehiclesRequestValueObjectMapper
      .map({
        search
      })
      .toJSON()

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) => this._notFoundVehiclesErrorFactory(message),
        [GENERIC]: ({message}) => this._genericVehiclesErrorFactory(message)
      })
      .post(url, body)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._searchVehiclesResultsValueObjectFactory(data)
  }

  @AsyncInlineError()
  async getMakesWithModels() {
    const url = `${this._config.get('API_URL')}/vehicle-specs/makemodels`

    const [error, response] = await this._fetchOrFail.get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    return MakesWithModelsListVehiclesValueObject.createFromLiterals({value: data})
  }

  @inlineError
  async getSearchModels({filtersSearch}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const searchFilters = filtersSearch.getSearchFromFilters()

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(
            `[HTTPVehiclesRepository#getSearchModels] Models not found for makeId ${searchFilters.makeId}: ${message}`
          ),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(
            `[HTTPVehiclesRepository#getSearchModels] Error getting models for makeId ${searchFilters.makeId}: ${message}`
          )
      })
      .post(`${this._config.get('API_URL')}/vehicle-specs/v1/models/search/listing`, {
        filters: {...searchFilters}
      })

    if (error) return Promise.reject(error)

    const {data} = response

    return ListModelVehiclesValueObject.createFromApi(data)
  }

  @AsyncInlineError()
  async getPriceRangeOptionsConfigurator() {
    const [error, response] = await this._fetchOrFail.get(
      `${this._config.get('API_URL')}/vehicle-specs/v1/price-ranges/configurator`
    )

    if (error) return Promise.reject(error)

    const {data} = response

    return ListPriceRangeOptionsValueObject.createFromApiResponse(data)
  }

  @AsyncInlineError()
  async getBodyTypesOptionsConfigurator({filterPriceSearchValueObject}) {
    const priceValue = filterPriceSearchValueObject.value
    const queryString = this._toQueryString({
      minPrice: priceValue.from,
      maxPrice: priceValue.to
    })

    const url = `${this._config.get('API_URL')}/vehicle-specs/v2/body-types/configurator?${queryString}`

    const [error, response] = await this._fetchOrFail.get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    return ListBodyTypesOptionsValueObject.createFromApiResponse(data)
  }

  @AsyncInlineError()
  async getFuelTypesOptionsConfigurator({price, bodyTypeIds}) {
    const priceValue = price.value
    const queryString = this._toQueryString({
      minPrice: priceValue.from,
      maxPrice: priceValue.to,
      bodyTypeIds: bodyTypeIds.value.join(',')
    })

    const url = `${this._config.get('API_URL')}/vehicle-specs/v2/fuel-types/configurator?${queryString}`

    const [error, response] = await this._fetchOrFail.get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    const result = ListFuelTypesOptionsValueObject.createFromApiResponse(data)
    return result
  }

  @AsyncInlineError()
  async getSeatsOptionsConfigurator({
    filterPriceSearchValueObject,
    filterBodyTypeIdsSearchValueObject,
    filterFuelTypeIdsSearchValueObject
  }) {
    const priceValue = filterPriceSearchValueObject.value
    const bodyTypeIds = filterBodyTypeIdsSearchValueObject.value
    const fuelTypeIds = filterFuelTypeIdsSearchValueObject.value

    const queryParams = {
      minPrice: priceValue.from,
      maxPrice: priceValue.to
    }

    if (bodyTypeIds && bodyTypeIds.length > 0) {
      queryParams.bodyTypeIds = bodyTypeIds.join(',')
    }

    if (fuelTypeIds && fuelTypeIds.length > 0) {
      queryParams.fuelTypeIds = fuelTypeIds.join(',')
    }

    const queryString = this._toQueryString(queryParams)
    const url = `${this._config.get('API_URL')}/vehicle-specs/v1/seats/configurator?${queryString}`

    const [error, response] = await this._fetchOrFail.get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    const result = ListSeatsOptionsValueObject.createFromApiResponse(data)
    return result
  }

  @AsyncInlineError()
  async getMakesOptionsConfigurator({
    filterPriceSearchValueObject,
    filterBodyTypeIdsSearchValueObject,
    filterFuelTypeIdsSearchValueObject,
    filterSeatsValueObject
  }) {
    const priceValue = filterPriceSearchValueObject.value
    const bodyTypeIds = filterBodyTypeIdsSearchValueObject.value
    const fuelTypeIds = filterFuelTypeIdsSearchValueObject.value
    const seats = filterSeatsValueObject.seats

    const queryParams = {
      minPrice: priceValue.from,
      maxPrice: priceValue.to
    }

    if (bodyTypeIds && bodyTypeIds.length > 0) {
      queryParams.bodyTypeIds = bodyTypeIds.join(',')
    }

    if (fuelTypeIds && fuelTypeIds.length > 0) {
      queryParams.fuelTypeIds = fuelTypeIds.join(',')
    }

    if (seats && seats.length > 0) {
      queryParams.seats = seats.join(',')
    }

    const queryString = this._toQueryString(queryParams)
    const url = `${this._config.get('API_URL')}/vehicle-specs/v1/makes/configurator?${queryString}`

    const [error, response] = await this._fetchOrFail.get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    const result = ListMakesOptionsValueObject.createFromApiResponse(data)
    return result
  }

  @AsyncInlineError()
  async getSuggestedVehicles(getSuggestedVehiclesRequest) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const queryString = getSuggestedVehiclesRequest.toQueryString()
    const url = `${this._config.get('API_URL')}/vehicle-specs/v1/vehicle-suggestions/configurator${
      queryString ? `?${queryString}` : ''
    }`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getSuggestedVehicles] not found: ${message}`),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(`[HTTPVehiclesRepository#getSuggestedVehicles] error: ${message}`)
      })
      .get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    return SuggestedVehiclesValueObject.createFromApiResponse(data)
  }

  @AsyncInlineError()
  async getVehicleImages({getImagesVehiclesRequest}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {vehicleId} = getImagesVehiclesRequest.toJSON()

    const url = `${this._config.get('API_URL')}/vehicle-specs/v1/images/vehicle/${vehicleId}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleImages] not found: ${message}`),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleImages] error: ${message}`)
      })
      .get(url)

    if (error) return Promise.reject(error)

    return ListImagesValueObject.createFromVehicleImages(response.data)
  }

  @AsyncInlineError()
  async getModelImages({getImagesVehiclesRequest}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {modelId} = getImagesVehiclesRequest.toJSON()

    const url = `${this._config.get('API_URL')}/vehicle-specs/v1/images/model/${modelId}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getModelImages] not found: ${message}`),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(`[HTTPVehiclesRepository#getModelImages] error: ${message}`)
      })
      .get(url)

    if (error) return Promise.reject(error)

    return ListImagesValueObject.createFromVehicleImages(response.data)
  }

  @AsyncInlineError()
  async getVehicleImage({getImagesVehiclesRequest}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {vehicleId, angle} = getImagesVehiclesRequest.toJSON()

    const url = `${this._config.get('API_URL')}/vehicle-specs/v1/images/vehicle/${vehicleId}/${angle}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleImage] not found: ${message}`),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(`[HTTPVehiclesRepository#getVehicleImage] error: ${message}`)
      })
      .get(url)

    if (error) return Promise.reject(error)

    return ImageValueObject.createFromVehicleImage(response.data)
  }

  @AsyncInlineError()
  async getModelImage({getImagesVehiclesRequest}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {modelId, angle} = getImagesVehiclesRequest.toJSON()

    const url = `${this._config.get('API_URL')}/vehicle-specs/v1/images/model/${modelId}/${angle}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: ({message}) =>
          this._notFoundVehiclesErrorFactory(`[HTTPVehiclesRepository#getModelImage] not found: ${message}`),
        [GENERIC]: ({message}) =>
          this._genericVehiclesErrorFactory(`[HTTPVehiclesRepository#getModelImage] error: ${message}`)
      })
      .get(url)

    if (error) return Promise.reject(error)

    return ImageValueObject.createFromVehicleImage(response.data)
  }
}
